"use client";

import {CSSProperties, JSX, useState} from "react";

import {useKeenSlider} from "keen-slider/react";

import {SpaceProps, useSectionSpace} from "@/sections/use-section-space";

import {ButtonBase} from "@/shared/ui/buttons";
import {Rating, StarRating} from "@/shared/ui/data-display/rating";
import {SingleReview} from "@/shared/ui/data-display/reviews";
import {Typography} from "@/shared/ui/data-display/typography";
import {ContainerBase} from "@/shared/ui/layout/container-base";
import {FloatingDropdown} from "@/shared/ui/overlay/floating-dropdown";

import "./review-section.scss";
import "keen-slider/keen-slider.min.css";

type ReviewItem = {
    name: string;
    rating: number;
    date: string;
    reviewText: string;
    managerResponse?: string;
};

type RatingBreakdownData = {
    fiveStar: number;
    fourStar: number;
    threeStar: number;
    twoStar: number;
    oneStar: number;
};

export interface ReviewSectionProps {
    title?: string;
    averageRating: number;
    totalReviews: number;
    reviews: Array<ReviewItem>;
    breakdown?: RatingBreakdownData;
    space?: SpaceProps;
    className?: string;
    headerCardView?: boolean;
    headerLabel?: string;
}

export function ReviewSection({
    title = "Reviews",
    averageRating,
    totalReviews,
    reviews,
    space,
    className = "",
    headerCardView = false,
    headerLabel,
}: ReviewSectionProps): JSX.Element | null {
    const sectionVars = useSectionSpace(space);

    const [sliderRef] = useKeenSlider<HTMLDivElement>({
        renderMode: "performance",
        rubberband: true,
        slides: {perView: 4, spacing: 16},
        breakpoints: {
            "(max-width: 767px)": {
                slides: {perView: 1.25, spacing: 16},
            },
        },
    });

    // Write a review popup state
    const [isOpen, setIsOpen] = useState(false);
    const [draftRating, setDraftRating] = useState(0);
    const [hoverRating, setHoverRating] = useState<number | null>(null);
    const [draftText, setDraftText] = useState("");

    if (!reviews?.length) return null;

    return (
        <section className={`review-section ${className}`} style={sectionVars}>
            <ContainerBase>
                <div className="review-section__header">
                    <Typography as="h2" size={{desktop: 20, mobile: 18}} weight={500} color="title" className="review-section__title">
                        {title}({totalReviews})
                    </Typography>

                    {headerCardView ? (
                        <div className="review-section__rating">
                            <div
                                className="star-rating__stars stars--large"
                                style={{"--rating": Math.max(0, Math.min(5, averageRating))} as React.CSSProperties}
                            />
                            <Typography as="span" size={14} weight={400} color="paragraph2" className="review-section__score">
                                {averageRating}/5
                            </Typography>
                        </div>
                    ) : (
                        <div className="review-section__rating">
                            <StarRating name={headerLabel ?? "Average"} rating={averageRating} className="review-section__stars" size="large" />
                            <div className="review-section__rating_value">
                                <Rating value={averageRating} size="small" />
                                <Typography as="span" size={14} weight={400} color="paragraph2">
                                    &nbsp;/5
                                </Typography>
                            </div>
                        </div>
                    )}
                </div>

                {/* Slider on all viewports (4 desktop / 1 mobile) */}
                <div ref={sliderRef} className="review-section__slider keen-slider">
                    {reviews.map((item, idx) => (
                        <div className="keen-slider__slide" key={`${item.name}-${idx}-slide`}>
                            <div className="review-section__card">
                                <SingleReview {...item} cardView />
                            </div>
                        </div>
                    ))}
                </div>

                {/* Footer with Write a Review */}
                <div className="review-section__footer">
                    <FloatingDropdown
                        isOpen={isOpen}
                        onOpenChange={setIsOpen}
                        placement="top-end"
                        dropdownClassName="review-section__dropdown"
                        trigger={<ButtonBase onClick={() => setIsOpen((v) => !v)}>Write a Review</ButtonBase>}
                    >
                        <div className="review-section__popup">
                            <div className="review-section__popup-head">
                                <Typography as="h3" size={{desktop: 20, mobile: 18}} weight={500} color="title">
                                    Write a Review
                                </Typography>
                                <button type="button" className="review-section__popup-close" onClick={() => setIsOpen(false)} aria-label="Close">
                                    ✕
                                </button>
                            </div>

                            {/* Interactive stars (click to set) */}
                            <div
                                className="star-rating__stars stars--large review-section__popup-stars"
                                style={{"--rating": hoverRating ?? draftRating} as CSSProperties}
                                onMouseMove={(e) => {
                                    const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                                    const x = e.clientX - rect.left;
                                    const ratio = Math.min(1, Math.max(0, x / rect.width));
                                    const value = Math.max(1, Math.ceil(ratio * 5));
                                    setHoverRating(value);
                                }}
                                onMouseLeave={() => setHoverRating(null)}
                                onClick={(e) => {
                                    const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                                    const x = e.clientX - rect.left;
                                    const ratio = Math.min(1, Math.max(0, x / rect.width));
                                    const value = Math.max(1, Math.ceil(ratio * 5));
                                    setDraftRating(value);
                                }}
                            />

                            <textarea
                                className="review-section__popup-textarea"
                                placeholder="Add Text here"
                                value={draftText}
                                onChange={(e) => setDraftText(e.target.value)}
                            />

                            <div className="review-section__popup-actions">
                                <ButtonBase onClick={() => setIsOpen(false)}>Submit</ButtonBase>
                            </div>
                        </div>
                    </FloatingDropdown>
                </div>
            </ContainerBase>
        </section>
    );
}
